/**
 * Fleet Ops Documents → API Responses (SCH-2814)
 *
 * The super-admin fleet pages read provisioning runs and dispatches live from the ops store
 * (`apps/ops-store/convex/fleetLive.ts`) instead of the backend's endpoints. These mappers turn the
 * store's documents into the response shapes those endpoints answer, so the pages keep their types.
 *
 * They mirror the backend's read path field for field: `runDetailOf` (run-store/convex-run-store.ts)
 * then `serializeRun` (provisioning/serialize.ts), and `dispatchRecordOf`
 * (browser-dispatch/store/convex-dispatch-store.ts) then `serializeDispatch`. A backend test feeds one
 * document through both and asserts they agree; change them together.
 *
 * No scrubbing here. Run inputs were scrubbed before the backend persisted them, and the store's content
 * scan (plan KTD13) refuses secret-shaped values, so a document holds secret stubs, never a raw secret.
 * Timestamps pass through: the store keeps them in `toISOString()` form, which is what the backend's
 * `Date` round trip produces. Pure, with no Node built-ins, so a browser bundle can import it.
 */

import type { BrowserSessionSlug } from '../../schemas/entities/provisioning-browser-steps'
import type { ProvisioningDispatchResponse } from '../../schemas/entities/provisioning-dispatch'
import type {
  ProvisioningRunResponse,
  ProvisioningRunResponseInputs,
  ProvisioningStepResponse
} from '../../schemas/entities/provisioning'
import type { FleetProvisioningDispatchDocument, FleetProvisioningRunWithSteps } from './fleet-ops'

/** A run or step error in the response envelope, as `serializeRun` builds it. */
function errorOf(value: unknown): ProvisioningRunResponse['error'] {
  if (value === null || value === undefined) return null
  if (typeof value === 'object' && 'type' in value) {
    const e = value as { type: unknown; message?: unknown; details?: unknown }
    return { type: String(e.type), message: typeof e.message === 'string' ? e.message : '', details: e.details }
  }
  return { type: 'UNKNOWN', message: String(value) }
}

function stepToResponse(s: FleetProvisioningRunWithSteps['steps'][number]): ProvisioningStepResponse {
  return {
    id: s.publicId,
    stepName: s.stepName,
    stepOrder: s.stepOrder,
    status: s.status,
    attemptCount: s.attemptCount,
    output: s.output,
    error: errorOf(s.error),
    startedAt: s.startedAt,
    completedAt: s.completedAt,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt
  }
}

/** A run document with its steps → `ProvisioningRunResponse`. */
export function fleetRunToResponse(run: FleetProvisioningRunWithSteps): ProvisioningRunResponse {
  return {
    id: run.publicId,
    mobileAppId: run.mobileAppPublicId,
    initiatedByUserId: run.initiatedByUserPublicId,
    status: run.status,
    inputs: run.inputs as ProvisioningRunResponseInputs,
    error: errorOf(run.error),
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    createdAt: run.createdAt,
    updatedAt: run.updatedAt,
    steps: run.steps.map(stepToResponse)
  }
}

/**
 * A dispatch document → `ProvisioningDispatchResponse`. The store-only fields (`legacyId`, `schoolId`,
 * `leaseExpiresAt`) stay behind, the error keeps only `{ type, message }`, and `requestedByUserId` is
 * withheld as `null`, as `serializeDispatch` withholds it.
 */
export function fleetDispatchToResponse(d: FleetProvisioningDispatchDocument): ProvisioningDispatchResponse {
  return {
    id: d.publicId,
    runId: d.runPublicId,
    mobileAppId: d.mobileAppPublicId,
    stepIds: d.stepIds,
    sessionSlug: d.sessionSlug as BrowserSessionSlug,
    status: d.status,
    stepStates: d.stepStates,
    error: d.error ? { type: d.error.type, message: d.error.message } : null,
    requestedByUserId: null,
    startedAt: d.startedAt,
    completedAt: d.completedAt,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt
  }
}
